import React, { useCallback, useEffect, useState } from 'react'

import {
  Alert,
  Button,
  Grid,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { PageTitle } from '../../components/PageElements'
import { useNotification } from '../../hooks/useNotification.js'
import services from '../../services/index.js'

import { trackPromise } from 'react-promise-tracker'
import { Popconfirm } from 'antd'
import { useParams } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Add, ArrowDownward, ArrowUpward, Close, Delete, Save } from '@mui/icons-material'
import Liberacoes from './Liberacoes'
import Consumo from './Consumo'

const MAX_SUGESTOES = 10
const IDENTIFICADOR = /^[A-Za-z_][A-Za-z0-9_$]{0,254}$/

type Origem = 'snowflake' | 'fabric'

function AiAgentEdit() {
  const { id } = useParams()
  const { notify } = useNotification()

  const [carregado, setCarregado] = useState(false)
  const [origemSalva, setOrigemSalva] = useState<Origem | null>(null)
  const [snowflakeConfigurado, setSnowflakeConfigurado] = useState<boolean | null>(null)

  const [name, setNewName] = useState('')
  const [instructions, setInstructions] = useState('')
  const [origem, setOrigem] = useState<Origem>('snowflake')
  const [azureAgentId, setNewAzureAgentId] = useState('')
  const [snowflakeDatabase, setSnowflakeDatabase] = useState('')
  const [snowflakeSchema, setSnowflakeSchema] = useState('')
  const [snowflakeAgent, setSnowflakeAgent] = useState('')
  const [limite, setLimite] = useState('')
  const [sugestoes, setSugestoes] = useState<string[]>([])

  const getAgent = useCallback(async () => {
    try {
      let res = await trackPromise(services.api.getAgent(id))
      if (res.fail) {
        throw new Error(res.error || 'Não foi possível consultar o agente')
      }
      const a = res.data
      const o: Origem = a.snowflakeAgent ? 'snowflake' : 'fabric'
      setNewName(a.name || '')
      setInstructions(a.instructions || '')
      setNewAzureAgentId(a.azureAgentId || '')
      setSnowflakeDatabase(a.snowflakeDatabase || '')
      setSnowflakeSchema(a.snowflakeSchema || '')
      setSnowflakeAgent(a.snowflakeAgent || '')
      setLimite(a.monthlyTokenLimit != null ? String(a.monthlyTokenLimit) : '')
      setSugestoes(a.suggestions || [])
      setOrigem(o)
      setOrigemSalva(o)
      setCarregado(true)
    } catch (error: any) {
      notify(error.message, { variant: 'error' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    getAgent()
    services.api.oneflowStatus().then((res: any) => {
      if (!res.fail) setSnowflakeConfigurado(!!res.data?.configurado)
    })
  }, [getAgent])

  const erroIdentificador = (v: string) => v.trim() !== '' && !IDENTIFICADOR.test(v.trim())
  const limiteNumero = limite.trim() === '' ? null : Number(limite)
  const limiteInvalido = limiteNumero !== null && (!Number.isInteger(limiteNumero) || limiteNumero <= 0)
  const snowflakeCompleto = !!(snowflakeDatabase.trim() && snowflakeSchema.trim() && snowflakeAgent.trim())
  const podeSalvar = !!name.trim() && !limiteInvalido && (origem === 'snowflake'
    ? snowflakeCompleto && ![snowflakeDatabase, snowflakeSchema, snowflakeAgent].some(erroIdentificador)
    : !!azureAgentId.trim())

  const saveEditedItem = useCallback(async () => {
    try {
      const snowflake = origem === 'snowflake'
      let data = {
        id: Number(id),
        name: name.trim(),
        instructions: instructions.trim() || null,
        azureAgentId: azureAgentId.trim() || null,
        snowflakeDatabase: snowflake ? snowflakeDatabase.trim() : null,
        snowflakeSchema: snowflake ? snowflakeSchema.trim() : null,
        snowflakeAgent: snowflake ? snowflakeAgent.trim() : null,
        monthlyTokenLimit: limiteNumero,
        suggestions: sugestoes.map(s => s.trim()).filter(s => s)
      }
      let res = await trackPromise(services.api.updateAiAgent(data))
      if (res.fail) {
        throw new Error(res.error || 'Não foi possível salvar os dados do agente')
      }
      setOrigemSalva(origem)
      setSugestoes(data.suggestions)
      notify('Agente salvo', { variant: 'success' })
    } catch (error: any) {
      notify(error.message, { variant: 'error' })
    }
  }, [id, name, instructions, origem, azureAgentId, snowflakeDatabase, snowflakeSchema, snowflakeAgent, limiteNumero, sugestoes, notify])

  const deleteAgent = useCallback(async () => {
    try {
      let res = await trackPromise(services.api.deleteAiAgent(id))
      if (res.fail) {
        throw new Error(res.error || 'Não foi possível excluir o agente')
      }
      window.location.href = `/app/dash_management/agents`
    } catch (error: any) {
      notify(error.message, { variant: 'error' })
    }
  }, [id, notify])

  const trocarSugestao = (i: number, texto: string) => setSugestoes(s => s.map((x, j) => (j === i ? texto : x)))
  const moverSugestao = (i: number, passo: number) => setSugestoes(s => {
    const n = [...s]
    const [item] = n.splice(i, 1)
    n.splice(i + passo, 0, item)
    return n
  })

  return (
    <div className="wrapper-container-content bgwhite">
      <div className="wrapper-page-content" id="wrapper-page-content">
        <PageTitle subText={`Editando agente #${id}`}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>{name ? `Agente ${name}` : 'Agente > ...'}</div>
          </div>
        </PageTitle>

        <Grid container justifyContent="center" spacing={{ xs: 3 }}>
          <Grid item md={11} xs={12}>
            <div className="panel">
              <div className="panel-body">

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <ArrowBackIcon sx={{ fontSize: 40, marginRight: '10px', cursor: 'pointer' }}
                                 onClick={() => window.location.href = `/app/dash_management/agents`} />
                  <h2>Dados do agente</h2>
                </div>

                {origem === 'snowflake' && snowflakeConfigurado === false && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    A conta e o token do Snowflake ainda não estão no segredo do Portal. Até isso ser feito, o
                    oneflow responde que não está disponível.
                  </Alert>
                )}

                <Grid container spacing={{ xs: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      size="small"
                      label="Nome"
                      inputProps={{ maxLength: 120 }}
                      InputLabelProps={{ shrink: true }}
                      value={name}
                      onChange={e => setNewName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ToggleButtonGroup size="small" exclusive value={origem} onChange={(_, v) => v && setOrigem(v)}>
                      <ToggleButton value="snowflake">Snowflake (Cortex Agent)</ToggleButton>
                      <ToggleButton value="fabric">Fabric (legado)</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>

                  {origem === 'snowflake' ? (
                    <>
                      {[
                        ['Database', snowflakeDatabase, setSnowflakeDatabase],
                        ['Schema', snowflakeSchema, setSnowflakeSchema],
                        ['Agente', snowflakeAgent, setSnowflakeAgent]
                      ].map(([rotulo, valor, mudar]: any) => (
                        <Grid item xs={12} md={4} key={rotulo}>
                          <TextField
                            fullWidth
                            required
                            size="small"
                            label={rotulo}
                            InputLabelProps={{ shrink: true }}
                            value={valor}
                            error={erroIdentificador(valor)}
                            helperText={erroIdentificador(valor) ? 'Use letras, números, _ ou $, começando por letra ou _' : ' '}
                            onChange={e => mudar(e.target.value)}
                          />
                        </Grid>
                      ))}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        label="Fabric URL (Azure)"
                        inputProps={{ maxLength: 1200 }}
                        InputLabelProps={{ shrink: true }}
                        value={azureAgentId}
                        onChange={e => setNewAzureAgentId(e.target.value)}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Instruções"
                      helperText="Vão junto com cada pergunta. O recorte de dados de quem pergunta é acrescentado pelo Portal."
                      inputProps={{ maxLength: 4000 }}
                      multiline
                      minRows={3}
                      InputLabelProps={{ shrink: true }}
                      value={instructions}
                      onChange={e => setInstructions(e.target.value)}
                    />
                  </Grid>

                  {origem === 'snowflake' && (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          label="Limite mensal do agente (tokens)"
                          InputLabelProps={{ shrink: true }}
                          value={limite}
                          error={limiteInvalido}
                          helperText={limiteInvalido ? 'Use um número inteiro maior que zero' : 'Soma de todas as pessoas. Vazio = sem limite.'}
                          onChange={e => setLimite(e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <div style={{ fontWeight: 'bold', marginBottom: 6 }}>Perguntas sugeridas</div>
                        <div style={{ fontSize: 12, color: '#6b7a80', marginBottom: 8 }}>
                          Aparecem quando a conversa está vazia. Até {MAX_SUGESTOES}, na ordem abaixo.
                        </div>
                        {sugestoes.map((s, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                            <TextField fullWidth size="small" value={s} inputProps={{ maxLength: 200 }}
                                       placeholder="Ex.: Quais lojas tiveram a maior ruptura no mês?"
                                       onChange={e => trocarSugestao(i, e.target.value)} />
                            <IconButton size="small" disabled={i === 0} onClick={() => moverSugestao(i, -1)} aria-label="Subir">
                              <ArrowUpward fontSize="small" />
                            </IconButton>
                            <IconButton size="small" disabled={i === sugestoes.length - 1} onClick={() => moverSugestao(i, 1)} aria-label="Descer">
                              <ArrowDownward fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setSugestoes(x => x.filter((_, j) => j !== i))} aria-label="Remover">
                              <Close fontSize="small" />
                            </IconButton>
                          </div>
                        ))}
                        <Button size="small" startIcon={<Add />} disabled={sugestoes.length >= MAX_SUGESTOES}
                                onClick={() => setSugestoes(x => [...x, ''])}>
                          Adicionar pergunta
                        </Button>
                      </Grid>
                    </>
                  )}
                </Grid>

                <Grid item xs={12} className="flex justify-end gap-6">
                  <div style={{ textAlign: 'right', marginTop: '10px' }}>
                    <Popconfirm
                      title="Excluir este agente? Ele sai dos dashboards e as liberações dele são removidas."
                      cancelText={'Cancelar'} okText={'Excluir'} onConfirm={deleteAgent}>
                      <Button
                        variant="contained"
                        className="btn-red mr-6 content-end mb-12"
                        type="button"
                        size="small"
                        color="inherit"
                      >
                        <Delete style={{ marginRight: '5px' }} fontSize="small" /> Excluir agente
                      </Button>
                    </Popconfirm>
                  </div>

                  <div style={{ textAlign: 'right', marginTop: '10px' }}>
                    <Button
                      variant="contained"
                      className="btn-purple mr-6 content-end mb-12"
                      type="button"
                      size="small"
                      color="inherit"
                      disabled={!carregado || !podeSalvar}
                      onClick={saveEditedItem}
                    >
                      <Save style={{ marginRight: '5px' }} fontSize="small" /> Salvar alterações
                    </Button>
                  </div>
                </Grid>

              </div>
            </div>
          </Grid>

          {origemSalva === 'snowflake' && (
            <>
              <Grid item md={11} xs={12}>
                <div className="panel">
                  <div className="panel-body">
                    <Liberacoes agenteId={Number(id)} />
                  </div>
                </div>
              </Grid>
              <Grid item md={11} xs={12}>
                <div className="panel">
                  <div className="panel-body">
                    <Consumo agenteId={Number(id)} />
                  </div>
                </div>
              </Grid>
            </>
          )}
        </Grid>

      </div>
    </div>
  )
}

export default AiAgentEdit
